/**
 * Warning component for duplicate spot detection
 */
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, X } from "lucide-react";
import { Button, Alert, AlertDescription } from "@tabipla/ui";
import type { DuplicateCheckResponseDto } from "@tabipla/shared";

interface DuplicateWarningProps {
  duplicateData: DuplicateCheckResponseDto;
  onAddAnyway: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function DuplicateWarning({
  duplicateData,
  onAddAnyway,
  onCancel,
  isLoading,
}: DuplicateWarningProps) {
  const { t } = useTranslation();

  // Build the warning message
  const messages: string[] = [];

  if (duplicateData.existsInTrip && duplicateData.tripSpot) {
    messages.push(
      t("spots.duplicateInTrip", {
        day: duplicateData.tripSpot.dayNumber,
        defaultValue: `${duplicateData.tripSpot.dayNumber}日目に追加済み`,
      })
    );
  }

  if (duplicateData.existsInMekke && duplicateData.mekkeSpot) {
    const statusLabel =
      duplicateData.mekkeSpot.status === "visited"
        ? t("mekke.visited", { defaultValue: "訪問済み" })
        : t("mekke.wantToGo", { defaultValue: "行きたい" });
    messages.push(
      t("spots.duplicateInMekke", {
        status: statusLabel,
        defaultValue: `Mekkeに保存済み（${statusLabel}）`,
      })
    );
  }

  if (duplicateData.existsInFavorites && duplicateData.favoriteSpot) {
    messages.push(t("spots.duplicateInFavorites", { defaultValue: "お気に入りに保存済み" }));
  }

  return (
    <Alert variant="warning">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        <div className="space-y-3">
          <div>
            <p className="font-medium">
              {t("spots.duplicateDetected", { defaultValue: "この場所は既に登録されています" })}
            </p>
            <ul className="mt-1 list-inside list-disc text-sm">
              {messages.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onAddAnyway}
              disabled={isLoading}
              className="border-amber-300 bg-white text-amber-700 hover:bg-amber-100"
            >
              <Check className="mr-1.5 h-3.5 w-3.5" />
              {t("spots.addAnyway", { defaultValue: "そのまま追加" })}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onCancel}
              disabled={isLoading}
              className="text-amber-700 hover:bg-amber-100"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              {t("common.cancel", { defaultValue: "キャンセル" })}
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Inline duplicate indicator (compact version)
 */
interface DuplicateIndicatorProps {
  duplicateData: DuplicateCheckResponseDto;
}

export function DuplicateIndicator({ duplicateData }: DuplicateIndicatorProps) {
  const { t } = useTranslation();

  const labels: string[] = [];

  if (duplicateData.existsInTrip) {
    labels.push(t("spots.inTrip", { defaultValue: "旅程内" }));
  }
  if (duplicateData.existsInMekke) {
    labels.push("Mekke");
  }
  if (duplicateData.existsInFavorites) {
    labels.push(t("spots.favorites", { defaultValue: "お気に入り" }));
  }

  if (labels.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 text-xs text-amber-600">
      <AlertTriangle className="h-3.5 w-3.5" />
      <span>
        {t("spots.alreadyIn", { defaultValue: "登録済み:" })} {labels.join(", ")}
      </span>
    </div>
  );
}
